// DOCS → REQUIREMENT SOURCES → THE BUILD PREVIEW — the gate BEFORE the build.
//
// A new sources version does not build straight away (heuristic-first, one card)
// any more: first a designer — ONE short fleet generation (a `preview` job on
// the project's requirements lane, a Claude Code session like every other Docs
// generation) — drafts a few VARIANTS of the change the sources ask for. Each
// variant is shown as a PICTURE first: its screen mockups, drawn by the one
// renderer (ScreenMockup) from the designer's widget trees, the way the
// showroom shows a use case as the application it would produce. Under the
// pictures: what the person gets in plain business language, the effort, and
// — folded away — the technical inventory (data, screens, workflows, agents,
// pipeline, out of scope) the builder will follow.
//
// The person reads, says what should be different (a comment on a variant, or
// a steer for the next round), regenerates — every previous round is KEPT and
// can be re-read — and APPROVES one. Only the approval files the build card,
// and the approved variant (mockups included) rides in its brief.
//
// State comes from GET /api/docs/preview (the version row's stage flag + the
// materials_preview rows); this component polls only while a round is being
// designed. Nothing here decides what is allowed — the backend does, and the
// panel renders the answer.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import html2canvas from "html2canvas";
import {
  Loader2, RefreshCw, Eye, Check, ChevronDown, ChevronRight, MessageSquarePlus,
  Rocket, AlertTriangle, FastForward, X, Maximize2, Pencil,
} from "lucide-react";
import { fetchData } from "../../fetchapi/fetchapi";
import ScreenMockup, { DESIGN_W, DESIGN_H } from "./ScreenMockup";
import { uploadOriginal } from "./upload";

// THE PICTURES TRAVEL WITH THE APPROVAL. A widget list is not a design: the
// builder that only read "kpis, chart, table" built a different app from the
// one the person approved (peakmade_v4). So on approval the browser rasterises
// the chosen variant's screens EXACTLY as this renderer draws them — full
// design width, 2× — uploads each PNG through the same presign handshake the
// requirement sources use, and the backend files them as IMAGE requirement
// sources and attaches them to the build card for the (multimodal) builder to
// look at.
//
// HOW: the screens are rendered into an ISOLATED same-origin iframe (no app
// stylesheet — Tailwind v4 paints every element's border-color in oklch(),
// which html2canvas cannot parse, and its resets would leak into the picture),
// then rasterised by Chrome's own text engine through an SVG <foreignObject>
// (pixel-exact: ellipses, chips, baselines). html2canvas is the FALLBACK for a
// browser that draws foreignObject blank (Safari).
const CAPTURE_BODY = 'margin:0;background:#f1f5f9;color:#0f172a;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.5;-webkit-font-smoothing:antialiased';
const CAPTURE_CSS = `html,body{${CAPTURE_BODY}}*{box-sizing:border-box}`;
const CAPTURE_SCALE = 2;
const CAPTURE_BG = "#f1f5f9";

function blankCanvas(c) {
  try {
    const g = c.getContext("2d");
    const step = Math.max(1, Math.floor(c.width / 40));
    const px = g.getImageData(0, Math.floor(c.height / 2), c.width, 1).data;
    for (let x = 0; x < c.width; x += step) {
      const o = x * 4;
      if (Math.abs(px[o] - 0xf1) > 8 || Math.abs(px[o + 1] - 0xf5) > 8 || Math.abs(px[o + 2] - 0xf9) > 8) return false;
    }
    return true;
  } catch { return true; }
}

async function rasterise(el, w, h) {
  try {
    const xml = new XMLSerializer().serializeToString(el);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><foreignObject width="100%" height="100%">`
      + `<div xmlns="http://www.w3.org/1999/xhtml" style="width:${w}px;height:${h}px;${CAPTURE_BODY}"><style>*{box-sizing:border-box}</style>${xml}</div>`
      + `</foreignObject></svg>`;
    const img = new Image();
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    await img.decode();
    const c = document.createElement("canvas");
    c.width = w * CAPTURE_SCALE; c.height = h * CAPTURE_SCALE;
    const g = c.getContext("2d");
    g.fillStyle = CAPTURE_BG; g.fillRect(0, 0, c.width, c.height);
    g.scale(CAPTURE_SCALE, CAPTURE_SCALE); g.drawImage(img, 0, 0);
    if (!blankCanvas(c)) {
      const blob = await new Promise((resolve) => c.toBlob(resolve, "image/png"));
      if (blob && blob.size > 2000) return { blob, via: "svg" };
    }
    rasterise.lastError = "foreignObject drew a blank picture";
  } catch (e) { rasterise.lastError = String(e?.message || e); }
  const canvas = await html2canvas(el, { scale: CAPTURE_SCALE, backgroundColor: CAPTURE_BG, logging: false, useCORS: true });
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  return { blob, via: "html2canvas", error: rasterise.lastError };
}

export async function captureMockups(v) {
  const plan = v?.plan || {};
  const mockups = Array.isArray(plan.mockups) ? plan.mockups : [];
  if (!mockups.length) return [];
  const fr = document.createElement("iframe");
  fr.setAttribute("aria-hidden", "true");
  fr.setAttribute("data-testid", "mockup-capture-frame");
  fr.style.cssText = `position:fixed;left:0;top:0;width:${DESIGN_W}px;height:${DESIGN_H}px;opacity:0;pointer-events:none;border:0;z-index:-1;`;
  document.body.appendChild(fr);
  const doc = fr.contentDocument;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><style>${CAPTURE_CSS}</style></head><body></body></html>`);
  doc.close();
  const mount = doc.createElement("div");
  mount.style.cssText = `width:${DESIGN_W}px;background:${CAPTURE_BG};`;
  doc.body.appendChild(mount);
  const root = createRoot(mount);
  const out = [];
  try {
    for (let i = 0; i < mockups.length; i++) {
      await new Promise((resolve) => {
        root.render(<ScreenMockup app={plan.app} screen={mockups[i]} />);
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
      const el = mount.firstElementChild;
      if (!el) continue;
      const h = Math.max(DESIGN_H, Math.round(el.offsetHeight || 0));
      const { blob, via, error } = await rasterise(el, DESIGN_W, h);
      if (blob) out.push({ index: i, title: mockups[i].title || `Screen ${i + 1}`, blob, via, error, width: DESIGN_W * CAPTURE_SCALE, height: h * CAPTURE_SCALE });
    }
  } finally {
    root.unmount();
    fr.remove();
  }
  return out;
}
// The e2e suite drives the capture on its own (a ready preview is a fleet
// round away; the rasteriser must be testable without one).
if (typeof window !== "undefined" && import.meta.env?.DEV) window.__cortexCaptureMockups = captureMockups;

const EFFORT = { S: "small — under half an hour of build", M: "medium — about an hour of build", L: "large — several hours of build" };
// ONE VOCABULARY with the backend (docs-model PREVIEW_LABELS): the preview is
// designed in cortex's own objects — sources, data elements, processing rules,
// source mappings, target tables, transformations, screens — with business
// processes, agents, models and services only where the sources ask. `data`
// and `pipeline` are legacy keys of previews stored before 2026-09-18.
const LISTS = [
  ["sources", "Data sources"], ["data_elements", "Data elements (the governed fields)"],
  ["rules", "Processing rules (the rule library a consultant reads, challenges and changes)"],
  ["mappings", "Source mappings (per source: which columns feed which data elements)"],
  ["targets", "Target model tables"], ["transformations", "Transformations (pipeline steps, by strategy kind)"],
  ["screens", "Screens"], ["workflows", "Business processes (only where the sources describe one)"],
  ["agents", "Agents (only where the sources name an automated decision)"], ["models", "ML models (the Models surface)"],
  ["services", "Services (components in their own runtime)"], ["out_of_scope", "Excluded by the sources"],
  ["data", "Data (legacy preview)"], ["pipeline", "Pipeline (legacy preview)"],
];

const BTN = "inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 disabled:opacity-50 disabled:cursor-not-allowed";

function VariantCard({ v, approvedId, canAct, busy, onApprove, onComment, onEnlarge, onEdit, onAttachPictures }) {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [txt, setTxt] = useState("");
  // EDIT MODE: every text of the variant is the person's to change before
  // approving — the mockups are not (regenerate for those).
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const isApproved = approvedId === v.id;
  const plan = v.plan || {};
  const mockups = Array.isArray(plan.mockups) ? plan.mockups : [];
  const hasInventory = LISTS.some(([k]) => (plan[k] || []).length);
  const startEdit = () => {
    setDraft({
      title: v.title || "", summary: v.summary || "", differs: plan.differs || "", product_name: plan.app?.product_name || "",
      ...Object.fromEntries(LISTS.map(([k]) => [k, (plan[k] || []).join("\n")])),
    });
    setEditing(true); setDetails(true);
  };
  const saveEdit = async () => {
    await onEdit(v, draft);
    setEditing(false); setDraft(null);
  };
  if (editing && draft) {
    const field = (k, label, rows = 2) => (
      <label className="block text-[11px] text-page-muted">
        {label}
        <textarea value={draft[k]} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} rows={rows} disabled={busy}
          data-testid={`preview-edit-${k}-${v.id}`}
          className="mt-0.5 w-full text-xs px-2 py-1 rounded-md border border-page-divider bg-card-bg text-page-text" />
      </label>
    );
    return (
      <div data-testid={`preview-variant-${v.id}`} data-variant-key={v.variant_key} className="rounded-lg border border-nav-active/60 p-3 space-y-2">
        <div className="text-[11px] font-semibold text-page-muted inline-flex items-center gap-1"><Pencil size={11} /> Editing variant {v.variant_key} — the screens stay as designed</div>
        {field("title", "Title", 1)}
        {field("product_name", "Application name", 1)}
        {field("summary", "What you get (plain business language)", 4)}
        {field("differs", "How it differs from the other variants", 2)}
        <div className="grid gap-2 sm:grid-cols-2">
          {LISTS.filter(([k]) => !["data", "pipeline"].includes(k) || (plan[k] || []).length).map(([k, label]) => field(k, `${label} (one per line)`, 3))}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={saveEdit} disabled={busy} className={BTN} data-testid={`preview-edit-save-${v.id}`}><Check size={12} /> Save</button>
          <button type="button" onClick={() => { setEditing(false); setDraft(null); }} className="text-[11px] text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer">Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <div data-testid={`preview-variant-${v.id}`} data-variant-key={v.variant_key}
      className={`rounded-lg border p-3 space-y-2.5 ${isApproved ? "border-emerald-500/60 bg-emerald-500/5" : v.status === "superseded" ? "border-page-divider opacity-70" : "border-page-divider"}`}>
      {/* THE PICTURE first */}
      {mockups.length ? (
        <div className="space-y-1.5">
          {mockups.map((sc, i) => (
            <button type="button" key={i} onClick={() => onEnlarge({ v, index: i })}
              data-testid={`preview-mockup-${v.id}-${i}`}
              title={`${sc.title} — click to enlarge`}
              className="block w-full text-left rounded-md border border-page-divider overflow-hidden bg-transparent p-0 cursor-zoom-in hover:border-nav-active/60 relative group">
              <ScreenMockup app={plan.app} screen={sc} />
              <span className="absolute bottom-1 left-1 right-1 flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-black/60 text-white">
                <span className="truncate flex-1">{sc.title}</span><Maximize2 size={10} className="opacity-70 group-hover:opacity-100" />
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-md border border-dashed border-page-divider p-3 text-[11px] text-page-muted" data-testid={`preview-nomockup-${v.id}`}>
          No screen mockup came back for this variant — regenerate to get one.
        </div>
      )}

      <div className="flex items-start gap-2 flex-wrap">
        <span className="shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-card-border/40 text-page-muted">{v.variant_key}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-page-title">{v.title}</div>
          {plan.app?.product_name ? <div className="text-[11px] text-page-muted">as the application <b>{plan.app.product_name}</b></div> : null}
        </div>
        <span className="shrink-0 text-[11px] px-1.5 py-0.5 rounded-md border border-page-divider text-page-muted" title={EFFORT[plan.effort] || ""}>
          effort {plan.effort || "M"}
        </span>
        {isApproved ? (
          <span className="shrink-0 text-[11px] px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1">
            <Check size={11} /> approved
          </span>
        ) : null}
      </div>
      {/* The pictures did not all make it (the capture runs in the browser and can fail there): say so, offer to attach them now. */}
      {isApproved && mockups.length && (v.pictures || []).length < mockups.length ? (
        <div className="flex flex-wrap items-center gap-2 text-[11px] rounded-md px-2 py-1.5"
          style={{ background: "var(--severity-warning-bg,#fef3c7)", color: "var(--severity-warning-text,#92400e)" }} data-testid={`preview-pictures-missing-${v.id}`}>
          <AlertTriangle size={12} className="shrink-0" />
          <span className="flex-1 min-w-0">{(v.pictures || []).length} of {mockups.length} screen pictures reached the requirement sources and the build card — the builder needs them to match the design.</span>
          <button type="button" data-testid={`preview-attach-pictures-${v.id}`} disabled={busy} onClick={() => onAttachPictures(v)}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-current bg-transparent cursor-pointer disabled:opacity-50">
            <Maximize2 size={11} /> Attach screen pictures
          </button>
        </div>
      ) : null}
      {v.summary ? <div className="text-xs text-page-text whitespace-pre-wrap">{v.summary}</div> : null}
      {plan.differs ? <div className="text-[11px] text-page-muted"><b>How it differs:</b> {plan.differs}</div> : null}

      {/* the technical inventory, folded */}
      {hasInventory ? (
        <div>
          <button type="button" onClick={() => setDetails((d) => !d)} data-testid={`preview-details-${v.id}`}
            className="inline-flex items-center gap-1 text-[11px] text-page-muted hover:text-page-text bg-transparent border-0 p-0 cursor-pointer">
            {details ? <ChevronDown size={11} /> : <ChevronRight size={11} />} What gets built
          </button>
          {details ? (
            <div className="grid gap-2 sm:grid-cols-2 text-[11px] mt-1.5">
              {LISTS.filter(([k]) => (plan[k] || []).length).map(([k, label]) => (
                <div key={k}>
                  <div className="font-semibold uppercase tracking-wide text-page-muted">{label}</div>
                  <ul className="list-disc pl-4 text-page-text space-y-0.5">
                    {plan[k].map((x, i) => <li key={i}>{x}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {(v.comments || []).length ? (
        <div className="rounded-md px-2 py-1.5 space-y-1" style={{ background: "var(--table-stripe)" }} data-testid={`preview-comments-${v.id}`}>
          <div className="text-[11px] font-semibold text-page-muted">Should be different:</div>
          {v.comments.map((c, i) => <div key={i} className="text-[11px] text-page-text">— {c.text}</div>)}
        </div>
      ) : null}
      {canAct ? (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button type="button" data-testid={`preview-approve-${v.id}`} disabled={busy}
            onClick={() => onApprove(v)}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-transparent bg-nav-active text-white cursor-pointer hover:opacity-90 disabled:opacity-50">
            <Rocket size={13} /> Approve &amp; build this
          </button>
          <button type="button" disabled={busy} onClick={() => setOpen((o) => !o)} className={BTN}
            data-testid={`preview-comment-toggle-${v.id}`}>
            <MessageSquarePlus size={13} /> What should be different?
          </button>
          <button type="button" disabled={busy} onClick={startEdit} className={BTN} title="Edit the title, the summary and the lists before approving"
            data-testid={`preview-edit-${v.id}`}>
            <Pencil size={13} /> Edit text
          </button>
        </div>
      ) : null}
      {canAct && open ? (
        <div className="space-y-1.5">
          <textarea value={txt} onChange={(e) => setTxt(e.target.value)} rows={2} disabled={busy}
            data-testid={`preview-comment-text-${v.id}`}
            placeholder="e.g. drop the renewal letters, add a per-property alert threshold, the forecast must be weekly not monthly"
            className="w-full text-xs px-2 py-1.5 rounded-md border border-page-divider bg-card-bg text-page-text" />
          <div className="flex items-center gap-2">
            <button type="button" disabled={busy || !txt.trim()} className={BTN}
              data-testid={`preview-comment-save-${v.id}`}
              onClick={async () => { await onComment(v, txt.trim()); setTxt(""); setOpen(false); }}>
              <Check size={12} /> Add comment
            </button>
            <span className="text-[11px] text-page-muted">Comments steer the next regeneration and travel with the approved variant into the build.</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// The enlarged mockup: one screen at full design width, with its siblings to
// step through.
function Lightbox({ item, onClose }) {
  const [i, setI] = useState(item.index || 0);
  const plan = item.v.plan || {};
  const mockups = plan.mockups || [];
  const sc = mockups[i] || mockups[0];
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") setI((x) => (x + 1) % mockups.length); if (e.key === "ArrowLeft") setI((x) => (x - 1 + mockups.length) % mockups.length); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mockups.length, onClose]);
  if (!sc) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={onClose} data-testid="preview-lightbox">
      <div className="bg-card-bg rounded-xl shadow-2xl max-w-[1180px] w-full max-h-full overflow-auto p-3 space-y-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 text-xs">
          <span className="font-semibold text-page-title">{item.v.variant_key} · {item.v.title}</span>
          <span className="text-page-muted">— {sc.title}{sc.caption ? `: ${sc.caption}` : ""}</span>
          <span className="flex-1" />
          {mockups.length > 1 ? (
            <span className="inline-flex items-center gap-1">
              {mockups.map((m, k) => (
                <button type="button" key={k} onClick={() => setI(k)}
                  className={`text-[11px] px-2 py-0.5 rounded-md border cursor-pointer ${k === i ? "border-nav-active text-nav-active" : "border-page-divider text-page-muted"} bg-transparent`}>
                  {m.title}
                </button>
              ))}
            </span>
          ) : null}
          <button type="button" onClick={onClose} className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1" data-testid="preview-lightbox-close"><X size={14} /></button>
        </div>
        <ScreenMockup app={plan.app} screen={sc} />
      </div>
    </div>
  );
}

export default function BuildPreviewPanel({ projectSlug, onChanged }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [steer, setSteer] = useState("");
  const [openRounds, setOpenRounds] = useState(() => new Set());
  const [enlarged, setEnlarged] = useState(null);
  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      const r = await fetchData("docs/preview", { method: "GET" });
      if (alive.current) setP(r?.ok ? r : null);
    } catch { if (alive.current) setP(null); }
  }, []);
  useEffect(() => { alive.current = true; setP(null); load(); return () => { alive.current = false; }; }, [load, projectSlug]);

  const state = p?.state || "none";
  const rounds = p?.rounds || [];
  // Poll while the designer works — the person is watching.
  useEffect(() => {
    if (state !== "generating") return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [state, load]);

  // Nothing to show: no version, no preview stage ever started (legacy), or a
  // dismissed version with no variants to look back on.
  if (!p || !p.materials_version) return null;
  if (state === "none") return null;
  if (state === "dismissed" && !rounds.length) return null;
  // PREVIEW ONLY ONCE (2026-09-04): a version after the shape was settled has
  // no preview of its own. With the approved design carried forward it stays
  // on the page as the record; without one, a line says where the sources went.
  if (state === "not_needed" && !rounds.length) {
    return (
      <div className="rounded-lg border border-page-divider px-3 py-2 text-xs text-page-muted inline-flex items-center gap-2"
        data-testid="build-preview" data-preview-state="not_needed">
        <Eye size={13} className="shrink-0" />
        <span>Sources v{p.materials_version}: no build preview — the shape of the project is already settled (an approved preview, or an application that exists). New sources go straight into the requirements review (<b>Requirements</b> tab).</span>
      </div>
    );
  }

  // ONCE APPROVED, ONLY THE CHOSEN VARIANT STAYS. The siblings and the earlier
  // rounds were the choice; the record is what was chosen (Alex, 2026-09-04).
  const approvedVariant = p.approved_id ? rounds.flatMap((r) => r.variants).find((v) => v.id === p.approved_id) || null : null;
  const latest = approvedVariant
    ? { ...(rounds.find((r) => r.variants.some((v) => v.id === approvedVariant.id)) || rounds[0]), variants: [approvedVariant], approvedOnly: true }
    : (rounds[0] || null);
  const older = approvedVariant ? [] : rounds.slice(1);
  const canAct = !p.approved_id && !p.request_id && state !== "generating" && state !== "not_needed";

  const regenerate = async () => {
    setBusy("regen"); setMsg("");
    try {
      const r = await fetchData("docs/preview/generate", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feedback: steer.trim() }),
      });
      if (r?.already_running) setMsg("A round is already being designed — watching it.");
      else { setMsg(`Designing round ${r?.round || ""} on the fleet — previous rounds stay below.`); setSteer(""); }
      await load();
    } catch (e) { setMsg(String(e?.message || "Could not design another round.")); await load(); }
    finally { setBusy(""); }
  };
  // Rasterise + upload the variant's screens; returns the upload records the
  // backend turns into image sources + card attachments. Throws on failure.
  const uploadPictures = async (v) => {
    const shots = await captureMockups(v);
    if (!shots.length && (v.plan?.mockups || []).length) throw new Error("no screen could be rasterised in this browser");
    const out = [];
    for (const sh of shots) {
      const safe = String(sh.title).replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 60);
      const file = new File([sh.blob], `approved-screen-${sh.index + 1}-${safe}.png`, { type: "image/png" });
      const key = await uploadOriginal(file, projectSlug);
      out.push({ index: sh.index, title: sh.title, s3_key: key, name: file.name, bytes: file.size, content_type: "image/png" });
    }
    return out;
  };
  // The retry, for an approved variant whose pictures did not all get through.
  const attachPictures = async (v) => {
    setBusy(`pictures-${v.id}`); setMsg("Capturing the approved screens…");
    try {
      const mockups = await uploadPictures(v);
      const r = await fetchData(`docs/preview/${v.id}/screens`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mockups }) });
      setMsg(`${r?.added || 0} screen picture${r?.added === 1 ? "" : "s"} attached — ${r?.card_updated ? "added to the build card and " : ""}filed as requirement sources${r?.materials_version ? ` (v${r.materials_version})` : ""}.`);
      await load(); onChanged?.();
    } catch (e) { setMsg(`The screen pictures could not be attached: ${String(e?.message || e).slice(0, 160)}`); }
    finally { setBusy(""); }
  };
  const approve = async (v) => {
    setBusy(`approve-${v.id}`); setMsg("");
    // 1. the pictures: rasterise + upload the screens (best effort — a stand
    //    whose canvas or S3 is down still approves, and says so LOUDLY; the
    //    approved card then offers "Attach screen pictures").
    let mockups = [];
    let pictureNote = "";
    try {
      setMsg("Capturing the approved screens…");
      mockups = await uploadPictures(v);
    } catch (e) {
      mockups = [];
      pictureNote = ` WARNING: the screen pictures could not be captured in this browser (${String(e?.message || e).slice(0, 120)}) — press "Attach screen pictures" on the approved variant (or approve from Chrome) so the builder gets them.`;
    }
    try {
      const r = await fetchData(`docs/preview/${v.id}/approve`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mockups }),
      });
      const id = r?.heuristic?.request_id;
      const pics = (r?.material?.mockups || []).length;
      setMsg(id
        ? `Approved variant ${v.variant_key} — build #${id} filed to the Board${r?.material?.id ? `, and the approved design was added to the requirement sources (v${r.material.materials_version}${pics ? `, ${pics} screen picture${pics === 1 ? "" : "s"} attached` : ""})` : ""}. The requirements analysis opens when it is done.${pictureNote}`
        : `Approved.${pictureNote}`);
      await load(); onChanged?.();
    } catch (e) { setMsg(String(e?.message || "Could not approve.")); }
    finally { setBusy(""); }
  };
  const edit = async (v, draft) => {
    setBusy(`edit-${v.id}`); setMsg("");
    try {
      await fetchData(`docs/preview/${v.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(draft) });
      setMsg(`Variant ${v.variant_key} updated — your text is what gets approved and built.`);
      await load();
    } catch (e) { setMsg(String(e?.message || "Could not save the edit.")); }
    finally { setBusy(""); }
  };
  const comment = async (v, text) => {
    setBusy(`comment-${v.id}`); setMsg("");
    try {
      await fetchData(`docs/preview/${v.id}/comment`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
      await load();
    } catch (e) { setMsg(String(e?.message || "Could not add the comment.")); }
    finally { setBusy(""); }
  };
  const skip = async () => {
    if (!window.confirm("Build straight from the sources without an approved preview?\n\nThe builder decides the shape itself. You can still analyse the requirements afterwards.")) return;
    setBusy("skip"); setMsg("");
    try {
      const r = await fetchData("docs/preview/skip", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
      const id = r?.heuristic?.request_id;
      setMsg(id ? `Build #${id} filed to the Board without a preview.` : "Build filed.");
      await load(); onChanged?.();
    } catch (e) { setMsg(String(e?.message || "Could not file the build.")); }
    finally { setBusy(""); }
  };
  const dismiss = async () => {
    if (!window.confirm("Close the preview for this version without building?\n\nUse this when the change needs no build (a retired note, a wording fix). The requirements analysis opens; no card is filed.")) return;
    setBusy("dismiss"); setMsg("");
    try {
      await fetchData("docs/preview/dismiss", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
      setMsg("Preview closed — nothing to build for this version.");
      await load(); onChanged?.();
    } catch (e) { setMsg(String(e?.message || "Could not close the preview.")); }
    finally { setBusy(""); }
  };

  const toggleRound = (r) => setOpenRounds((s) => { const n = new Set(s); if (n.has(r)) n.delete(r); else n.add(r); return n; });
  const cardProps = { approvedId: p.approved_id, canAct, busy: !!busy, onApprove: approve, onComment: comment, onEnlarge: setEnlarged, onEdit: edit, onAttachPictures: attachPictures };

  return (
    <div className="rounded-xl border-2 p-4 space-y-3" style={{ borderColor: "var(--nav-active, #4f46e5)" }}
      data-testid="build-preview" data-preview-state={state}>
      {enlarged ? <Lightbox item={enlarged} onClose={() => setEnlarged(null)} /> : null}
      <div className="flex items-start gap-2 flex-wrap">
        <div className="text-sm font-medium inline-flex items-center gap-1.5 text-page-title">
          <Eye size={15} /> Build preview — sources v{p.materials_version}
          {p.request_id ? (
            <span className="text-[11px] font-normal text-page-muted">· build #{p.request_id} filed{p.approved_id ? " from the approved variant" : ""}</span>
          ) : (
            <span className="text-[11px] font-normal text-page-muted">· nothing is built yet</span>
          )}
        </div>
        <div className="flex-1" />
        {state === "generating" ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-page-muted" data-testid="preview-generating">
            <Loader2 size={13} className="animate-spin" /> designing variants and their screens…
          </span>
        ) : null}
      </div>

      <div className="text-xs text-page-muted">
        {canAct
          ? "A few ways the sources could be built — every variant covers everything the sources ask for; they differ in the shape of the application, not in scope. Each is shown as the application it would produce. Click a screen to see it large. Pick one and approve it — that files the ONE build card with your choice — or say what should be different and design another round. Earlier rounds stay below."
          : state === "not_needed"
            ? `The approved design stays here as the record of the project's shape. Sources v${p.materials_version} needed no preview — new sources go straight into the requirements review (Requirements tab).`
          : p.request_id
            ? "The build was filed from this preview. The approved design stays here as the record of what was chosen."
            : state === "dismissed" ? "This version was closed without a build. The variants stay here for the record."
            : state === "generating" ? "The designer is drafting variants of the change — and a screen mockup for each — from the sources that changed in this version." : ""}
      </div>

      {state === "failed" ? (
        <div className="flex items-start gap-2 text-xs rounded-md px-3 py-2"
          style={{ background: "var(--severity-error-bg,#fee2e2)", color: "var(--severity-error-text,#991b1b)" }} data-testid="preview-failed">
          <AlertTriangle size={13} className="shrink-0 mt-[1px]" />
          <div className="flex-1 min-w-0"><b>The designer could not draft the variants.</b> {p.error || "No reason was recorded."} Try again, or build without a preview.</div>
        </div>
      ) : null}

      {latest ? (
        <div className="space-y-2" data-testid="preview-latest-round" data-round={latest.round} data-approved-only={latest.approvedOnly ? "1" : undefined}>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-page-muted">
            {latest.approvedOnly ? `Approved design (round ${latest.round})` : `Round ${latest.round}`}{latest.feedback && !latest.approvedOnly ? <span className="font-normal normal-case"> · designed from: “{latest.feedback}”</span> : null}
          </div>
          {/* Siblings are hidden once a variant is approved, so the approved
              design is alone in the round: give it the whole width (its
              mockups scale with the card — half a row made them thumbnails). */}
          <div className={latest.approvedOnly ? "grid gap-3" : "grid gap-3 lg:grid-cols-3"} data-testid="preview-round-grid">
            {latest.variants.map((v) => <VariantCard key={v.id} v={v} {...cardProps} />)}
          </div>
        </div>
      ) : null}

      {canAct || state === "failed" ? (
        <div className="rounded-lg border border-page-divider p-3 space-y-2" style={{ background: "var(--table-stripe)" }}>
          <div className="text-xs font-medium text-page-title">Want it different? Say what to change and design another round.</div>
          <textarea value={steer} onChange={(e) => setSteer(e.target.value)} rows={2} disabled={!!busy}
            data-testid="preview-steer"
            placeholder="e.g. one lean variant with only the executive dashboard; keep the renewal workflow but no agents; everything must fit a one-hour build"
            className="w-full text-xs px-2 py-1.5 rounded-md border border-page-divider bg-card-bg text-page-text" />
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" data-testid="preview-regenerate" onClick={regenerate} disabled={!!busy || state === "generating"} className={BTN}>
              {busy === "regen" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              {busy === "regen" ? "Designing…" : latest ? "Regenerate variants" : "Design variants"}
            </button>
            <button type="button" data-testid="preview-skip" onClick={skip} disabled={!!busy}
              className="inline-flex items-center gap-1 text-[11px] text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer disabled:opacity-50">
              <FastForward size={12} /> Build without a preview
            </button>
            <button type="button" data-testid="preview-dismiss" onClick={dismiss} disabled={!!busy}
              className="inline-flex items-center gap-1 text-[11px] text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer disabled:opacity-50">
              Nothing to build for this version
            </button>
          </div>
        </div>
      ) : null}

      {older.length ? (
        <div className="space-y-1" data-testid="preview-history">
          {older.map((r) => (
            <div key={r.round} className="rounded-md border border-page-divider">
              <button type="button" onClick={() => toggleRound(r.round)}
                data-testid={`preview-round-toggle-${r.round}`}
                className="w-full flex items-center gap-1.5 text-xs px-2 py-1.5 bg-transparent border-0 cursor-pointer text-page-muted hover:text-page-text text-left">
                {openRounds.has(r.round) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                Round {r.round} · {r.variants.length} variant(s){r.feedback ? ` · “${r.feedback}”` : ""}
              </button>
              {openRounds.has(r.round) ? (
                <div className="grid gap-3 lg:grid-cols-3 p-2 pt-0">
                  {r.variants.map((v) => <VariantCard key={v.id} v={v} {...cardProps} />)}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {msg ? <div className="text-xs text-page-muted" data-testid="preview-msg">{msg}</div> : null}
    </div>
  );
}
